import { useCallback, useEffect, useRef, useState } from "react";
import {
  RequestGetSyncItemsStautsRequest,
  RequestUpsertListingsItemsRequest,
  ResponseSyncItemsStatusResponse,
} from "../../services/OpenAPI";
import { useApiGetSyncItemsStatus } from "../api/useApiGetSyncItemsStatus";
import { useApiUpsertListingsItems } from "../api/useApiUpsertListingsItems";
import { lwaNotConnectedMessage } from "../../utils/lwaError";

export type ListingsSyncSeverity = "success" | "error" | "warning" | "info";

// 同期状態のポーリング間隔
const POLL_INTERVAL_MS = 5000;
// 同期開始直後の確認までの待ち時間（出品数が少ないと数秒で終わるため早めに確認する）
const FIRST_CHECK_DELAY_MS = 1500;

// トーストは HTML として表示されるため、サーバーのエラー内容はエスケープする
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// 同期結果を利用者向けのメッセージに変換する
export const buildListingsSyncMessage = (
  status: ResponseSyncItemsStatusResponse
): { message: string; severity: ListingsSyncSeverity } => {
  if (status.result === "not_connected") {
    return { message: lwaNotConnectedMessage(), severity: "error" };
  }
  if (status.result !== "success") {
    const detail = status.message ? `<br />（${escapeHtml(status.message)}）` : "";
    return {
      message: `商品情報の同期に失敗しました。時間をおいて再度お試しください。${detail}`,
      severity: "error",
    };
  }

  const imported = status.importedCount ?? 0;
  const skipped = status.skippedCount ?? 0;
  if (imported > 0) {
    const skippedNote =
      skipped > 0 ? `<br />※自己配送の出品 ${skipped} 件は対象外です。` : "";
    return {
      message: `商品情報の同期が完了しました（${imported} 件）。${skippedNote}`,
      severity: "success",
    };
  }
  if (skipped > 0) {
    // 出品はあるが、すべて「出品者から出荷」のため FBA 納品の対象にならないケース
    return {
      message:
        `FBA 対象の出品がありませんでした。<br />` +
        `自己配送（出品者から出荷）の出品 ${skipped} 件は対象外です。<br />` +
        `FBA 納品する商品は、セラーセントラルで「Amazonから出荷」に変更してから再度同期してください。`,
      severity: "warning",
    };
  }
  return {
    message: "セラーセントラルに出品が見つかりませんでした。",
    severity: "warning",
  };
};

type UseListingsSyncParams = {
  tenantId?: number;
  userId?: number;
  // false の間は同期状態をポーリングしない（閉じているダイアログなど）。既定は true
  enabled?: boolean;
  // 同期の開始・完了などを利用者に知らせる
  onMessage: (message: string, severity: ListingsSyncSeverity) => void;
  // 開始した同期が完了したとき（成功・失敗とも）に呼ばれる
  onCompleted?: (status: ResponseSyncItemsStatusResponse) => void;
};

// セラーセントラルの商品情報同期。
// 同期はバックグラウンドで実行されるため、開始後は同期状態をポーリングして完了を待ち、結果を通知する。
export const useListingsSync = ({
  tenantId,
  userId,
  enabled = true,
  onMessage,
  onCompleted,
}: UseListingsSyncParams) => {
  const { getSyncItemsStatus } = useApiGetSyncItemsStatus();
  const { upsertListingsItems, isLoading: isStartingSync } =
    useApiUpsertListingsItems();
  const [isSyncing, setIsSyncing] = useState(false);

  // この画面から開始した（または開始を確認した）同期の完了を待っているか
  const awaitingRef = useRef(false);
  // ポーリングのコールバックから常に最新の関数を呼べるようにする
  const onMessageRef = useRef(onMessage);
  const onCompletedRef = useRef(onCompleted);
  onMessageRef.current = onMessage;
  onCompletedRef.current = onCompleted;

  const checkStatus = useCallback(async () => {
    const req: RequestGetSyncItemsStautsRequest = { tenantId, userId };
    const status = await getSyncItemsStatus(req);
    if (!status) return;

    const syncing = status.isSyncing ?? false;
    setIsSyncing(syncing);

    // 結果は同期開始時にクリアされるため、同期中でなく結果があれば今回の同期が完了している
    if (awaitingRef.current && !syncing && status.result) {
      awaitingRef.current = false;
      const { message, severity } = buildListingsSyncMessage(status);
      onMessageRef.current(message, severity);
      onCompletedRef.current?.(status);
    }
  }, [tenantId, userId]);

  useEffect(() => {
    if (!enabled) return;
    checkStatus();
    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkStatus, enabled]);

  // 同期の完了待ちに入る（検索で同期が始まった場合にも使う）
  const awaitSync = useCallback(() => {
    awaitingRef.current = true;
    setIsSyncing(true);
    setTimeout(checkStatus, FIRST_CHECK_DELAY_MS);
  }, [checkStatus]);

  const startSync = async () => {
    const req: RequestUpsertListingsItemsRequest = { tenantId, userId };
    const { ok, notConnected, alreadyRunning } = await upsertListingsItems(req);
    if (notConnected) {
      onMessageRef.current(lwaNotConnectedMessage(), "error");
      return;
    }
    if (!ok && !alreadyRunning) {
      onMessageRef.current("商品情報の同期を開始できませんでした。", "error");
      return;
    }
    onMessageRef.current(
      alreadyRunning
        ? "商品情報の同期を実行中です。完了までお待ちください。"
        : "商品情報の同期を開始しました。完了するとお知らせします。",
      "info"
    );
    awaitSync();
  };

  // 「同期中を解除」したときなど、同期中表示と完了待ちを取り消す
  const cancelAwaitSync = () => {
    awaitingRef.current = false;
    setIsSyncing(false);
  };

  return {
    isSyncing,
    isStartingSync,
    startSync,
    awaitSync,
    cancelAwaitSync,
  };
};
